import * as Sentry from '@sentry/react';

/**
 * Keep React alive when a page translator rewrites the DOM underneath it.
 *
 * ## Why this exists
 *
 * Chrome's built-in page translation replaces text nodes with its own nested
 * `<font>` wrappers. React does not know that happened: its fibers still point
 * at the original text nodes, so the next unmount calls `removeChild` on a node
 * that now has a different parent and the DOM throws
 *
 *     NotFoundError: Failed to execute 'removeChild' on 'Node':
 *                    The node to be removed is not a child of this node.
 *
 * which reaches the ErrorBoundary and takes the page down. That is
 * `WEFAQNA-FRONTEND-9`, and the breadcrumb naming the culprit is unambiguous —
 * the click landed on `span#radix-:r2d: > font > font`, and **we never render a
 * `<font>` element anywhere**. The member was on `/groom-form`, part-way
 * through building his profile, and lost it.
 *
 * We serve an Arabic-first site to people across several countries, so being
 * translated is normal and expected. Suppressing translation with
 * `translate="no"` would "fix" the crash by removing a feature members are
 * deliberately using.
 *
 * ## What this does
 *
 * Makes the two mutation methods React relies on non-fatal in exactly the case
 * that currently throws: the node is not where the caller believes it is.
 * Everything else is passed straight through to the native implementation, so
 * normal rendering is untouched.
 *
 * This is the mitigation React's own long-running issue on the subject settles
 * on, and it is deliberately narrow: no wholesale reimplementation, and it only
 * intervenes on the throwing path.
 *
 * A breadcrumb rather than an event, so an error that DOES still occur carries
 * the context, without spending Sentry quota on every translated session (the
 * 2026-06 quota work).
 *
 * Safe to call more than once.
 */
let installed = false;

export function installTranslationSafeDom() {
  if (installed) return false;
  if (typeof Node !== 'function' || !Node.prototype) return false;
  installed = true;

  const nativeRemoveChild = Node.prototype.removeChild;
  const nativeInsertBefore = Node.prototype.insertBefore;

  const note = (method) => {
    try {
      Sentry.addBreadcrumb({
        category: 'dom',
        level: 'warning',
        message: `${method} skipped: node re-parented by a page translator`,
      });
    } catch {
      /* breadcrumbs are best-effort */
    }
  };

  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      note('removeChild');
      return child;
    }
    return nativeRemoveChild.apply(this, arguments);
  };

  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      note('insertBefore');
      return newNode;
    }
    return nativeInsertBefore.apply(this, arguments);
  };

  return true;
}

export default installTranslationSafeDom;
